import React, { FC, RefObject } from 'react';

import styled from 'styled-components';

import { Account } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import { MenuItem } from '@adsk/alloy-react-menu';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import HighlightedItemText from './HighlightedItemText';
import { MFAIcon } from './MFAIcon';
import getAccountUrl from '../helpers/getAccountUrl';
import getAnchorRel from '../helpers/getAnchorRel';
import getAnchorTarget from '../helpers/getAnchorTarget';

const StyledImg = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 5px;
  margin-right: 16px;
`;

type ProjectListAccountItemProps = {
  item: Account;
  search?: string;
  currentAccountId?: string;
  currentProductId?: string;
  onClose?: () => void;
  onAccountClick?: (account?: object, url?: string) => void;
  containerRef?: RefObject<HTMLDivElement>;
};

const ProjectListAccountItem: FC<ProjectListAccountItemProps> = ({
  item: account,
  search,
  currentAccountId,
  currentProductId,
  onClose,
  onAccountClick,
  containerRef,
}) => {
  const url = getAccountUrl(account, currentProductId);
  const highlighted = currentAccountId === account.account_id;

  return (
    <MenuItem
      data-testid={`ProjectPicker__Accounts-${account.account_id}`}
      rel={getAnchorRel(url)}
      highlighted={highlighted}
      showSelection={false}
      as={onAccountClick ? 'button' : 'a'}
      href={url}
      target={getAnchorTarget(url)}
      onClick={
        onAccountClick
          ? () => {
              onAccountClick(account, url);
              onClose?.();
            }
          : onClose
      }
      style={{
        height: 'auto',
        padding: '8px 16px',
        textAlign: 'initial',
      }}
    >
      <Flex flex={1}>
        <StyledImg src={account.image_url} data-testid="item-image" />
        <Flex alignItems="center" flex={1}>
          <Box
            typography="heading4"
            color={highlighted ? 'white' : 'charcoal900'}
          >
            <OverflowTooltip
              content={account.account_display_name}
              singleLine={false}
            >
              <HighlightedItemText
                text={account.account_display_name}
                search={search}
                highlighted={highlighted}
              />
            </OverflowTooltip>
          </Box>
          {account.mfaBlocked && <MFAIcon containerRef={containerRef} />}
        </Flex>
      </Flex>
    </MenuItem>
  );
};

ProjectListAccountItem.displayName = 'ProjectListAccountItem';

export default ProjectListAccountItem;
